"use client"

import { AddressSection } from "@/(admin)/modules/ui-builder/config/components/AddressSection"
import { SocialSection } from "@/(admin)/modules/ui-builder/config/components/SocialSection"
import { Images } from "@/constants/images"
import { ADMIN_SITE_CONFIG } from "@/constants/site-configs"
import { useFooterSettings } from "@/hook/useFooterSettings"
import { getLocaleHomeHref } from "@/lib/locale-utils"
import { toImageSrc } from "@/lib/utils"
import { fetchTamdaGroupSetting } from "@/services/tamdaGroupSetting"
import { usePostIdStore } from "@/stores/use-post-id-store"
import { FooterGroupProps } from "@/types/types"
import Image, { StaticImageData } from "next/image"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { useEffect, useState } from "react"
import { twMerge } from "tailwind-merge"

interface TamdaGroupSettingAttributes {
  footerLogo?: {
    data?: {
      attributes?: {
        url?: string
      }
    }
  }
  facebookUrl?: string
  twitterUrl?: string
  youtubeUrl?: string
  copyright?: string
}

const FooterGroup: React.FC<FooterGroupProps> = ({
  locale,
  className,
  aboutLinks = [],
  infoLinks = [],
  office,
  about,
  info,
  copyrightFallback,
  settings: serverSettings,
}) => {
  // Server đã fetch settings và truyền qua prop → bỏ qua fetch client (zustand +
  // useEffect). Fallback client chỉ dùng khi không có server settings (ui-builder
  // preview).
  const hasServerSettings = serverSettings !== undefined
  const { settings: fetchedSettings } = useFooterSettings(locale, hasServerSettings)
  const settings = serverSettings ?? fetchedSettings

  const [footerLogoState, setFooterLogoState] = useState<string | StaticImageData>(Images.logoTamdaGroup)

  useEffect(() => {
    if (hasServerSettings) return // logo đã có từ server
    fetchTamdaGroupSetting(locale)
      .then(data => {
        const attrs = data?.attributes as TamdaGroupSettingAttributes | undefined
        const url = attrs?.footerLogo?.data?.attributes?.url
        if (url) setFooterLogoState(toImageSrc(url))
      })
      .catch(() => { })
  }, [locale, hasServerSettings])

  const footerLogoUrl = serverSettings?.footerLogoUrl ? toImageSrc(serverSettings.footerLogoUrl) : footerLogoState

  // copyrightFallback đã được server dịch theo locale; literal cuối chỉ để phòng khi
  // FooterGroup được dùng ngoài FooterServer (ui-builder preview).
  const copyright =
    settings?.copyright || copyrightFallback || `Bản quyền thuộc về Tamda Foods s.r.o © 2011 - ${new Date().getFullYear()}`
  const facebookUrl = settings?.facebookUrl
  const twitterUrl = settings?.twitterUrl
  const youtubeUrl = settings?.youtubeUrl

  const pathname = usePathname()
  const postDataMap = usePostIdStore(state => state.postDataMap)
  // const hasEditPermission = (() => {
  //   if (typeof window === "undefined") return false
  //   try {
  //     const permissions = JSON.parse(localStorage.getItem("permissions") || "{}")
  //     const canEditPost = permissions.post?.update?.enabled === true
  //     const canEditCategory = permissions.category?.update?.enabled === true
  //     return canEditPost || canEditCategory
  //   } catch {
  //     return false
  //   }
  // })()
  const segments = pathname?.split("/").filter(Boolean) || []
  const slug = segments.join("/")
  const postId = postDataMap[slug]?.id || null
  const showEditIcon = postId !== null
  //  hasEditPermission
  return (
    <footer className={twMerge(className)}>
      <div className="mx-auto w-full border-t border-[#242f3e33] pt-10 flex flex-col justify-center items-center">
        <Link href={getLocaleHomeHref(locale)} className="flex justify-center max-w-[1290px] w-full image-hover">
          <div className="relative w-[290px] h-[26px]">
            <Image src={footerLogoUrl} alt="Tamda Group" fill className="object-contain" />
          </div>
        </Link>

        <div className="flex flex-col sm:flex-row gap-[50px] pt-[50px] pb-[30px] max-w-[1290px] w-full px-[15px]">
          <AddressSection title={office} className="sm:w-[51.25%] w-full" locale={locale} />

          <div className="sm:w-[24.375%] w-full flex flex-col gap-[15px]">
            <h3 className="text-xl leading-[22px] font-bold text-[#18af4e]">{about}</h3>
            <ul className="flex flex-col">
              {aboutLinks.length > 0 &&
                aboutLinks.map((link, index) => (
                  <li key={index} className="flex flex-col">
                    <Link
                      href={link.url || "#"}
                      className="text-base sm:leading-[24px] leading-[26px] font-semibold text-[#52526C] py-[3px] hover-underline-left-to-right w-fit"
                    >
                      {link.title}
                    </Link>
                  </li>
                ))}
            </ul>
          </div>

          <div className="sm:w-[24.375%] w-full flex flex-col gap-[15px]">
            <h3 className="text-xl leading-[22px] font-bold text-[#18af4e]">{info}</h3>
            <ul className="flex flex-col">
              {infoLinks.length > 0 &&
                infoLinks.map((link, index) => (
                  <li key={index} className="flex flex-col">
                    <Link
                      href={link.url || "#"}
                      className="text-base sm:leading-[24px] leading-[26px] font-semibold text-[#52526C] py-[3px] hover-underline-left-to-right w-fit"
                    >
                      {link.title}
                    </Link>
                  </li>
                ))}
            </ul>
          </div>
        </div>

        <div className="py-5 border-t border-[#242f3e33] w-full flex justify-center items-center">
          <div className="max-w-[1290px] w-full flex flex-col md:flex-row justify-between px-[15px]">
            <p className="text-sm leading-[28px] font-normal text-[#333] text-center md:text-left">{copyright}</p>
            <div className="flex flex-row items-center gap-5">
              <SocialSection facebookUrl={facebookUrl} twitterUrl={twitterUrl} youtubeUrl={youtubeUrl} />
              {postId && showEditIcon && (
                <Link
                  href={`${ADMIN_SITE_CONFIG.ADMIN_BASE_URL}/admin/posts/edit?id=${postId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex relative"
                >
                  <Image src={Images.editIcon} alt="Edit" width={28} height={28} className="w-[28px] h-[28px]" />
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default FooterGroup
